import { defineStore } from 'pinia';
import ProductService from '@/services/product';
import { IntentService } from '@/services/intents';
import { useIntentsStore } from '@/stores/intents';

// status of creation process
export const statusTypes = {
  initial: 'initial',
  creatingWithTemplate: 'creatingWithTemplate',
  creatingWithoutTemplate: 'creatingWithoutTemplate',
  created: 'created',
};

/** Draft data for the intent being created */
interface NewIntentDraft {
  /** Technical intent name */
  intent: string;
  /** Human-readable name */
  displayName: string;
  /** Intent description */
  description: string;
  /** Scenario description (used for query validation) */
  scenario: string;
  /** Example user phrases */
  samples: string[];
  /** Template intent name this draft is based on ('' = none) */
  template: string;
}

// Clone this for initial and cleanup
const cleanIntent: NewIntentDraft = {
  intent: '',
  displayName: '',
  description: '',
  scenario: '',
  samples: ['', '', '', '', ''],
  template: ''
};

interface IntentNewState {
  /** Whether the "new intent" dialog/wizard is open */
  intentNewOpen: boolean;
  /** Current creation status; one of statusTypes */
  status: string;
  /** The intent draft being edited */
  newIntent: NewIntentDraft;
  /** Last search text; null when no search was done */
  searchRequest: string | null;
  /** Search results grouped by origin ({own, all, ...}); null when no search was done */
  searchResults: any | null;
  /** Current wizard step (1-based) */
  activeStep: number;
  templatesExpansion: number[]; // Vuetify logic `[0]` = open / [] = close (0 is index of 1st panel)
  createdIntent: string; // Intent string of created intent without template,
}

/**
 * Pinia conversion of the legacy vuex module `src/store/modules/intentNew.ts` (namespace
 * `intentNew`). Persisted to `localStorage` (full state) — matches the legacy `vuexLocal`
 * reducer's `intentNew: state.intentNew` entry in `src/store/index.ts`.
 *
 * Collisions & renames (see
 * `/private/tmp/claude-501/-Users-haslien-Documents-GitHub-moin-hub/23091b27-f9d9-4b85-91e6-ff6cd1c12710/scratchpad/pinia-migration/RENAMES.md`):
 * - `vuex-map-fields`' `getField`/`updateField` are DROPPED entirely — Pinia state is directly
 *   writable, so consumers replace `mapFields` bindings with writable `computed`/`storeToRefs`
 *   against this store.
 * - Getters `intentNewOpen`, `newIntent`, `status`, `searchRequest`, `searchResults`,
 *   `activeStep`, `templatesExpansion`, `createdIntent` were pure identity passthroughs of the
 *   state prop of the same name. Pinia exposes state props directly on the store instance
 *   (`useIntentNewStore().status`), so these getters are DROPPED entirely rather than renamed —
 *   consumers read the state prop directly, which is exactly what the old getter returned.
 * - No mutation/action or getter/action name collisions exist in this module (mutation and
 *   action name sets were already disjoint), so no `<name>Action` renames were needed.
 */
export const useIntentNewStore = defineStore('intentNew', {
  state: (): IntentNewState => ({
    intentNewOpen: false,
    status: statusTypes.initial,
    newIntent: { ...cleanIntent },
    searchRequest: null,
    searchResults: null,
    activeStep: 1,
    templatesExpansion: [], // Vuetify logic `[0]` = open / [] = close (0 is index of 1st panel)
    createdIntent: '', // Intent string of created intent without template,
  }),
  getters: {
    /**
     * pre-existing: 'isDisabled' is not part of the state — getter kept for compatibility.
     * Always returns `undefined`, exactly as the legacy getter did (it read `state.isDisabled`,
     * a property that has never existed on this module's state).
     */
    isDisabled(): any {
      return undefined;
    },
    /** Example user phrases from the current draft */
    samples(): string[] {
      return this.newIntent.samples;
    },
  },
  actions: {
    /**
     * Converted from legacy mutation `setIntentNewOpen`.
     */
    setIntentNewOpen(value: boolean) {
      this.intentNewOpen = value;
    },
    /**
     * Converted from legacy mutation `updateSamples`.
     */
    updateSamples(samples: string[]) {
      this.newIntent.samples = samples;
    },
    /**
     * Converted from legacy mutation `setStatus`.
     * Use statusTypes export as kind of enum
     */
    setStatus(value: string) {
      this.status = value;
    },
    /**
     * Converted from legacy mutation `setIntentWithTemplate`.
     */
    setIntentWithTemplate({
      intent,
      displayName,
      description = '',
      scenario = '',
      samples = [''],
      template,
    }: {
      intent: string;
      displayName: string;
      description?: string;
      scenario?: string;
      samples?: string[];
      template: string;
    }) {
      this.newIntent = {
        intent,
        displayName,
        description,
        samples,
        scenario,
        template
      };
      this.status = statusTypes.creatingWithTemplate;
    },
    /**
     * Converted from legacy mutation `setIntentWithoutTemplate`.
     */
    setIntentWithoutTemplate() {
      this.newIntent = { ...cleanIntent };
      this.status = statusTypes.creatingWithoutTemplate;
    },
    /**
     * Converted from legacy mutation `addSample`.
     */
    addSample() {
      this.newIntent.samples.push('');
    },
    /**
     * Converted from legacy mutation `deleteSample`.
     */
    deleteSample(index: number) {
      this.newIntent.samples.splice(index, 1);
    },
    /**
     * Converted from legacy mutation `setSearchRequest`.
     */
    setSearchRequest({ text, results }: { text: string; results: any }) {
      this.searchRequest = text;
      this.searchResults = results;
    },
    /**
     * Converted from legacy mutation `setSearchResults`.
     */
    setSearchResults({ results }: { results: any }) {
      this.searchResults = results;
    },
    /**
     * Converted from legacy mutation `clearSearch`.
     */
    clearSearch() {
      this.newIntent = { ...cleanIntent };
      this.activeStep = 1;
      this.searchRequest = null;
      this.searchResults = null;
      this.status = statusTypes.initial;
    },
    /**
     * Converted from legacy mutation `setActiveStep`.
     */
    setActiveStep(value: number) {
      this.activeStep = value;
    },
    /**
     * Converted from legacy mutation `setTemplatesExpansion`.
     */
    setTemplatesExpansion(value: number[]) {
      this.templatesExpansion = value;
    },
    /**
     * Converted from legacy mutation `setCreatedIntent`.
     */
    setCreatedIntent(value: string) {
      this.createdIntent = value;
    },
    /**
     * Converted from legacy mutation `reset`.
     */
    reset() {
      this.newIntent = { ...cleanIntent };
      this.activeStep = 1;
      this.searchRequest = null;
      this.searchResults = null;
      this.status = statusTypes.initial;
      this.templatesExpansion = [];
      this.createdIntent = '';
    },
    /**
     * Converted from legacy action `createNewIntent`.
     * Cross-namespace: legacy `commit('intents/setInLibrary', ..., {root:true})` and
     * `commit('intents/addIntent', ..., {root:true})` → `useIntentsStore()` called inside the
     * action body (never at module top level).
     * NOTE: legacy `commit('intents/setInLibrary', ...)` targeted a mutation that was never
     * registered in the legacy `intents` module (only `setLibrary`, with a different payload
     * shape, existed) — Vuex silently no-ops an unknown mutation type, so this was already a
     * latent dead call in production. Preserved as a no-op here rather than calling
     * `intentsStore.setLibrary(...)`, which would change behavior.
     */
    async createNewIntent({ botId }: { botId: string }) {
      const result: any = await ProductService.addNewIntent({
        botId,
        ...this.newIntent,
      });

      if (result.error || !result.intent) {
        return {
          success: false,
          result,
        };
      }
      const intentsStore = useIntentsStore();
      // intentsStore.setInLibrary(result.intent); -- legacy target never existed; latent no-op, preserved.
      intentsStore.addIntent(result.intent);
      this.setCreatedIntent(result.intent.intent);
      this.setStatus(statusTypes.created);
      return {
        success: true,
        result,
      };
    },
    /**
     * Converted from legacy action `validateQuery`.
     */
    async validateQuery({ botId, query }: { botId: string; query: string }) {
      if (query === '') return { valid: null };
      const result: any = await IntentService.validateQuery(botId, query, this.newIntent.displayName, this.newIntent.scenario);
      return { valid: result.valid, label: result.label };
    },
    /**
     * Converted from legacy action `searchStandard`.
     * Cross-namespace: legacy `rootGetters['intents/getIntentLibraryMap']` →
     * `useIntentsStore().getIntentLibraryMap` called inside the action body.
     */
    async searchStandard({ botId, text, language }: { botId: string; text: string; language: string }) {
      const intentLibrary = useIntentsStore().getIntentLibraryMap;

      let similarIntents: any = {};
      let searchResults: any = {};

      try {
        similarIntents = await IntentService.similarIntents(botId, text, language);
        searchResults = {
          own: similarIntents.topics.map((topic: any) => ({ label: topic.intent, confidence: 1 })),
          all: similarIntents.templateTopics.map((topic: any) => ({ label: topic.intent, confidence: 1 })),
        };

      } catch (e) {
        searchResults = await IntentService.searchIntent(botId, text);
      }

      // {own: Array, standard: Array, smalltalk: Array, all: Array}
      for (const [key, value] of Object.entries(searchResults)) {
        //if (key !== 'own') {
        searchResults[key] = (value as any[]).filter((intent) => {
          if (!intentLibrary[intent.label]) return false;
          if (intentLibrary[intent.label].standard && key === 'own') return false;
          if (!intentLibrary[intent.label].standard && key !== 'own')
            return false;
          return true;
        });
        //}
      }

      searchResults.newTopic = similarIntents.newTopic;
      this.setSearchRequest({ text, results: searchResults });
    },
  },
  vuexPersist: {
    local: true,
  },
});
